import { Anchor, Box, Button, Container, Divider, Group, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import React from 'react';

const LAST_UPDATED = '3 September 2026';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    const theme = useMantineTheme();
    return (
        <Stack gap="xs" mt="xl">
            <Title order={2} fz="lg" fw={500} c={theme.colors.yellow[9]}>
                {title}
            </Title>
            {children}
        </Stack>
    );
}

export default function Terms() {
    const theme = useMantineTheme();

    return (
        <Box bg="dark.8" py="xl">
            <Container size="lg" py="xl">
                <Group mb="lg">
                    <Button
                        component={Link}
                        to="/"
                        variant="filled"
                        color="yellow.9"
                        leftSection={<IconArrowLeft size={16} />}
                    >
                        Back to home
                    </Button>
                </Group>

                <Title ta="center" c="white">
                    Terms and Conditions
                </Title>
                <Text size="sm" ta="center" c="gray.5" mt={4}>
                    Last updated: {LAST_UPDATED}
                </Text>

                <Text size="xl" c="white" mt="md">
                    These Terms and Conditions ("Terms") govern the use of the website of{' '}
                    <Text span fw={600} c="yellow.9" inherit>
                        SIA IC Trading
                    </Text>{' '}
                    ("IC Trading", "we", "us", or "our") and the purchase of products and services offered by
                    us. By using our website, contacting us regarding our products, or placing an order, you
                    agree to these Terms.
                </Text>

                <Divider my="xl" color={theme.colors.dark[4]} />

                <Stack gap={0} c="gray.3" fz="sm">
                    <Section title="1. Company Information">
                        <Text fw={600} c="white">SIA IC Trading</Text>
                        <Text>Latvia</Text>
                        <Text>Office: 30 Terbatas Street, Riga, LV-1011, Latvia</Text>
                        <Text>Warehouse: 52 Uzvaras Street, Dobele, Latvia</Text>
                        <Text>
                            Email:{' '}
                            <Anchor href="mailto:hello.ictrading@gmail.com" c="yellow.9">
                                hello.ictrading@gmail.com
                            </Anchor>
                        </Text>
                        <Text>
                            Phone: <Anchor href="tel: +37120234693" c="yellow.9"> +371 2023 4693</Anchor>
                        </Text>
                    </Section>

                    <Section title="2. Products">
                        <Text>
                            IC Trading supplies oil and industrial products, including diesel fuel, heating diesel,
                            and industrial release and anti-adhesive agents.
                        </Text>
                        <Text>
                            Product descriptions, specifications, certificates, availability, and other information
                            published on our website are provided for general information and may be updated from
                            time to time.
                        </Text>
                        <Text>
                            Products are supplied in accordance with applicable quality, safety, excise, and other
                            legal requirements.
                        </Text>
                    </Section>

                    <Section title="3. Orders and Contracts">
                        <Text>Information displayed on the website does not constitute a binding offer to sell.</Text>
                        <Text>
                            An order or purchase request becomes binding only after it has been confirmed by
                            IC Trading in writing or otherwise accepted by both parties.
                        </Text>
                        <Text>
                            We reserve the right to refuse or cancel an order where, for example, the requested
                            product is unavailable, information has been provided incorrectly, applicable legal
                            requirements cannot be met, or the transaction cannot lawfully be completed.
                        </Text>
                        <Text>
                            For wholesale transactions, specific commercial terms may be agreed separately between
                            IC Trading and the customer.
                        </Text>
                    </Section>

                    <Section title="4. Prices and Payment">
                        <Text>Prices are provided upon request or as otherwise agreed with the customer.</Text>
                        <Text>
                            Unless expressly agreed otherwise, prices do not include applicable taxes, duties,
                            transport costs, or other charges.
                        </Text>
                        <Text>
                            Payment terms, payment deadlines, delivery costs, and other commercial conditions will
                            be agreed with the customer before or at the time of order confirmation.
                        </Text>
                        <Text>
                            IC Trading reserves the right to suspend delivery or further performance where an
                            invoice remains unpaid after its due date.
                        </Text>
                    </Section>

                    <Section title="5. Delivery">
                        <Text>
                            Delivery arrangements and estimated delivery dates will be agreed with the customer for
                            each order.
                        </Text>
                        <Text>
                            Delivery times may be affected by circumstances outside our reasonable control,
                            including transport delays, weather conditions, border restrictions, regulatory
                            requirements, fuel availability, or other logistical circumstances.
                        </Text>
                        <Text>
                            The applicable transfer of risk and responsibility for the goods will be determined by
                            the agreed delivery terms and, where applicable, the agreed Incoterms.
                        </Text>
                        <Text>
                            The customer is responsible for providing accurate delivery information and ensuring
                            that the receiving location is suitable and legally permitted for the relevant
                            products.
                        </Text>
                    </Section>

                    <Section title="6. Product Quality and Compliance">
                        <Text>
                            IC Trading takes reasonable measures to ensure that products supplied meet applicable
                            quality requirements and are accompanied by relevant manufacturer or quality
                            documentation where applicable.
                        </Text>
                        <Text>
                            Customers are responsible for using products in accordance with applicable laws,
                            regulations, product documentation, safety requirements, and manufacturer instructions.
                        </Text>
                        <Text>
                            Certain products may be subject to specific storage, handling, transport, excise,
                            environmental, or other legal requirements. The customer is responsible for complying
                            with requirements applicable to its possession or use of the products.
                        </Text>
                    </Section>

                    <Section title="7. Inspection and Claims">
                        <Text>
                            Customers should inspect products and, where reasonably possible, their quantity and
                            condition upon delivery.
                        </Text>
                        <Text>
                            Any apparent shortage, damage, or non-conformity should be reported to IC Trading
                            without undue delay and supported by relevant documentation or evidence.
                        </Text>
                        <Text>
                            Claims concerning product quality or conformity should be submitted within the period
                            required by applicable law and should include sufficient information to allow
                            IC Trading to investigate the claim.
                        </Text>
                        <Text>
                            Nothing in these Terms limits any mandatory rights or remedies available to customers
                            under applicable law.
                        </Text>
                    </Section>

                    <Section title="8. Website Content">
                        <Text>
                            We make reasonable efforts to ensure that information published on our website is
                            accurate and up to date. However, we do not guarantee that all website content will
                            always be complete, current, or free from errors.
                        </Text>
                        <Text>
                            We reserve the right to modify, update, or remove website content at any time without
                            prior notice.
                        </Text>
                    </Section>

                    <Section title="9. Intellectual Property">
                        <Text>
                            Unless otherwise stated, the content of this website, including text, graphics, logos,
                            photographs, and other materials, belongs to IC Trading or is used with permission.
                        </Text>
                        <Text>
                            Website content may not be copied, reproduced, distributed, modified, or commercially
                            used without our prior written permission, except where permitted by applicable law.
                        </Text>
                    </Section>

                    <Section title="10. Limitation of Liability">
                        <Text>
                            To the maximum extent permitted by applicable law, IC Trading shall not be liable for
                            indirect, incidental, consequential, or unforeseeable losses arising from the use of the
                            website or from a transaction, except where such liability cannot legally be excluded
                            or limited.
                        </Text>
                        <Text>
                            Nothing in these Terms excludes or limits liability that cannot be excluded or limited
                            under applicable law.
                        </Text>
                    </Section>

                    <Section title="11. Force Majeure">
                        <Text>
                            IC Trading shall not be responsible for failure or delay in performing its obligations
                            where such failure or delay results from circumstances beyond our reasonable control.
                        </Text>
                        <Text>
                            Such circumstances may include natural disasters, severe weather, war, governmental
                            restrictions, changes in legislation, transport disruptions, strikes, supply
                            interruptions, infrastructure failures, or other events that could not reasonably have
                            been prevented or overcome.
                        </Text>
                    </Section>

                    <Section title="12. Privacy">
                        <Text>
                            Personal data submitted through our website or during a business relationship with
                            IC Trading is processed in accordance with applicable data protection legislation and
                            our Privacy Policy.
                        </Text>
                    </Section>

                    <Section title="13. Applicable Law">
                        <Text>
                            These Terms and any contractual relationship between IC Trading and the customer shall
                            be governed by the laws of the Republic of Latvia, unless mandatory applicable law
                            provides otherwise.
                        </Text>
                        <Text>
                            Any disputes shall, where possible, first be resolved through good-faith negotiations.
                            If a dispute cannot be resolved amicably, it shall be submitted to the competent courts
                            of the Republic of Latvia, subject to applicable mandatory jurisdiction rules.
                        </Text>
                    </Section>

                    <Section title="14. Changes to These Terms">
                        <Text>
                            IC Trading may update these Terms from time to time. The latest version will be
                            published on this website and will indicate the date of the most recent update.
                        </Text>
                        <Text>Changes will apply from the date of publication unless otherwise stated.</Text>
                    </Section>

                    <Section title="15. Contact">
                        <Text>
                            If you have any questions regarding these Terms, our products, or our services, please
                            contact us:
                        </Text>
                        <Text fw={600} c="white" mt="xs">
                            SIA IC Trading
                        </Text>
                        <Text>30 Terbatas Street, Riga, LV-1011, Latvia</Text>
                        <Text>
                            Email:{' '}
                            <Anchor href="mailto:hello.ictrading@gmail.com" c="yellow.9">
                                hello.ictrading@gmail.com
                            </Anchor>
                        </Text>
                        <Text>
                            Phone: <Anchor href="tel: +37120234693" c="yellow.9"> +371 2023 4693</Anchor>
                        </Text>
                    </Section>
                </Stack>

                <Divider my="xl" color={theme.colors.dark[4]} />

                <Group justify="center">
                    <Button
                        component={Link}
                        to="/"
                        variant="filled"
                        color="yellow.9"
                        leftSection={<IconArrowLeft size={16} />}
                    >
                        Back to home
                    </Button>
                </Group>
            </Container>
        </Box>
    );
}